import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import {
  ActionsPopup,
  DatePicker,
  IStyles,
  LinkButton,
  PrimaryButton,
  ReminderPopup,
  SelectMenu,
  TimePicker,
  convertPercentageToMarks,
  deserify,
  formatSecondsToDateTimeString,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  remainingTimeInHoursAndMins,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Timestamp } from '@bufbuild/protobuf';
import { useLocation, useNavigate } from 'react-router-dom';
import { DifficultyLevelEnum } from '@protos/content_management/content.db_pb';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../reduxStore/reduxHooks';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import {
  combineStartTimeAndDate,
  convertIsoStringToTimestamp,
} from '../../../../utils/functions';
import { setToastInfo } from '../../../Home/reducer/homeDashboard.slice';
import { setCurrentAssessmentClassStats } from '../../reducer/manageHomework.slice';
import { OverviewBox } from '../../../../components/OverviewBox';
import { HeaderWithProgressBar } from '../../../../components/HeaderWithProgressBar';
import { ConnectedClassStartBox } from './ConnectedClassStartBox';
import {
  AttemptStats,
  AttemptTypeEnum,
  SessionStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { IOptionList } from '../../ClickerAssessmentEndedHw/components/CAEndedHwLeftPanel';
import {
  createIOptionListFromAttemptStats,
  isTimeLeft,
} from '../../manageHWutils';
import { AssessmentPageType } from '../../ClickerAssessmentManage';
import { ASSESSMENT_VIEW } from '../../../../routeHandling/RoutesNomenclature';
import { useClickerAssessmentContext } from '../../../../app/Context/ClickerAssessmentContextProvider';
import {
  IStatusObj,
  setCABaseStationDetails,
  setClickerAssessmentInfo,
  setSessionStartType,
} from '../../../Assessment/ClickerAssessment/reducer/clickerAssessment.slice';
import { CLICKER_ASSESSMENT_START } from '../../../../routeHandling/RoutesNomenclature';
import { interactionEvent } from '../../../Auth/auth.events';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(24), md: pxTovW(30) },
    width: { xs: '100%', md: '100%', lg: '100%' },
    boxSizing: 'border-box',
  },

  infoBox: {
    height: { md: 'max-content' },
    borderRadius: { md: pxToRem(10) },
    backgroundColor: '#FFFFFF',
    border: { md: '1px solid #E0DFDE' },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    boxSizing: 'border-box',
    boxShadow: `0px 0px ${pxToRem(10)} #0000001F`,
    paddingY: { xs: pxToRem(21), md: pxTovW(20), lg: pxTovW(30) },
    paddingX: { xs: pxToRem(35), md: pxTovW(20), lg: pxTovW(65) },
  },
  deadlineBox: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  timeDetails: {
    width: '50%',
    // padding: { xs: pxToRem(4), md: 0, lg: pxTovW(8) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(5), md: pxTovW(10) },
  },

  endExtendButtonBox: {
    display: 'flex',
    justifyContent: 'space-evenly',
  },
};

interface IProps {
  isLoading: 'loading' | 'error' | 'success';
  setIsLoading: Dispatch<SetStateAction<'loading' | 'error' | 'success'>>;
  handleEndTest: () => Promise<void>;
  pageType: AssessmentPageType;
  setIsAssessmentOngoing?: Dispatch<SetStateAction<boolean | null>>;
}
export const CAOngoingHwLeftPanel = (props: IProps) => {
  const {
    isLoading,
    setIsLoading,
    handleEndTest,
    pageType,
    setIsAssessmentOngoing,
  } = props;

  const {
    getBaseStationConnectionStatus,
    authenticateAnonymously,
    caBaseStationData,
  } = useClickerAssessmentContext();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const teacher_id = getLocalStorage('userId');
  const { setShowAppLoader, isIpadOnly } = useGlobalContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler,
  } = useCommonServiceClientContext();

  const { current_assessmemt_classStats, selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const { clicker_assessment_info, ca_base_station_details } = deserify(
    useAppSelector((state) => state.clickerAssessment)
  );

  const [endTestPopup, setEndTestPopup] = useState(false);

  const deadLine = current_assessmemt_classStats?.assessment
    ?.assessmentTargetDate
    ? new Timestamp(
        current_assessmemt_classStats?.assessment?.assessmentTargetDate
      ).toDate()
    : undefined;
  const localDate = new Date(
    Date.now() - new Date().getTimezoneOffset() * 60000
  );

  const [endTimeValues, setEndTimeValues] = useState({
    timeValue: `${String(localDate.getHours()).padStart(2, '0')} : ${String(
      localDate.getMinutes()
    ).padStart(2, '0')}`,
    dateValue: new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    ).toISOString(), // Includes timezone offset
  });
  const [extendDeadlinePopup, setExtendDeadlinePopup] = useState(false);

  // ------------------------------------------------------------

  const extendDeadlineCTA = async () => {
    new Timestamp(
      current_assessmemt_classStats?.assessment?.assessmentTargetDate
    ).toDate() < new Date(Date.now())
      ? dispatch(
          setToastInfo({
            label: 'Deadline already passed',
            variant: 'error',
            open: true,
          })
        )
      : setExtendDeadlinePopup(true);
    await interactionEvent({
      context: 'Clicker_assessment',
      name: 'EXTEND_DEADLINE',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };

  const handleExtendDeadline = async () => {
    setIsLoading('loading');
    try {
      const IsoStringDeadline =
        endTimeValues && combineStartTimeAndDate(endTimeValues);
      const IsoStringCurrentDeadline = new Timestamp(
        current_assessmemt_classStats?.assessment?.assessmentTargetDate
      )
        .toDate()
        .toISOString();
      const currentDeadlineTime = new Date(IsoStringCurrentDeadline);
      const NewendTime = new Date(IsoStringDeadline);
      const currentTime = new Date(Date.now());
      console.log({
        EndTime: IsoStringDeadline,
        STartTime: IsoStringCurrentDeadline,
      });
      if (NewendTime <= currentDeadlineTime) {
        dispatch(
          setToastInfo({
            label: 'Only Extension of deadline is allowed',
            variant: 'error',
            open: true,
          })
        );
        setIsLoading('error');
        return;
      } else {
        const response =
          await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.assessmentAssignmentSubmit(
            {
              assessmentId: selected_assessment_id,
              teacherId: current_assessmemt_classStats?.assessment?.teacherId,
              startTime:
                current_assessmemt_classStats?.assessment
                  ?.assessmentCommenceDate,
              endTime: convertIsoStringToTimestamp(IsoStringDeadline),
              assessmentInstructions:
                current_assessmemt_classStats?.assessment
                  ?.assessmentInstructions,
              sectionIds: current_assessmemt_classStats?.assessment?.sectionId
                ? [current_assessmemt_classStats?.assessment?.sectionId]
                : undefined,
              dailyReminderTime:
                current_assessmemt_classStats?.assessment?.dailyReminderTime,
              assignmentInfo:
                current_assessmemt_classStats?.assessment?.assignmentInfo,
            }
          );
        // console.log(response);
        setExtendDeadlinePopup(false);
        if (response.status === 200 && response.data?.updatedAssessment) {
          // dispatch(
          //   setCurrentAssessmentClassStats({
          //     ...current_assessmemt_classStats,
          //     assessment: response.data.updatedAssessment,
          //   })
          // );
          const latestCompletedAttempt =
            current_assessmemt_classStats?.attemptInfo
              .filter(
                (e) =>
                  e.attemptStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
              )
              .sort((a, b) => {
                return b.attemptNumber - a.attemptNumber;
              })[0];
          getAssessmentClassStats(latestCompletedAttempt);
          dispatch(
            setToastInfo({
              label: 'Deadline extended',
              variant: 'info',
              open: true,
            })
          );

          setIsLoading('success');
        } else {
          dispatch(
            setToastInfo({
              label: 'Deadline Extension failed',
              variant: 'error',
              open: true,
            })
          );
          setIsLoading('error');
        }
      }
    } catch (error) {
      setExtendDeadlinePopup(false);
      dispatch(
        setToastInfo({
          label: 'Deadline Extension failed',
          variant: 'error',
          open: true,
        })
      );
      console.log(error);
      setIsLoading('error');
    }
  };

  // ------------------------------------------------------------

  const connectedClassStartResumeCTA = async (
    startType: 'start' | 'resume'
  ) => {
    await interactionEvent({
      context: 'Clicker_assessment',
      name: startType.toUpperCase(),
      pathSegments: pathSegments,
      isOffline: isOffline,
    });

    const timeLeftToDoAssessment = isTimeLeft(
      new Timestamp(
        current_assessmemt_classStats?.assessment?.assessmentTargetDate
      )
    );
    if (!timeLeftToDoAssessment) {
      dispatch(
        setToastInfo({
          label: `Cannot ${startType} as Time has ended !`,
          variant: 'info',
          open: true,
        })
      );
      setIsAssessmentOngoing && setIsAssessmentOngoing(timeLeftToDoAssessment);
      return;
    }

    if (
      !current_assessmemt_classStats?.assessment?.classId ||
      !current_assessmemt_classStats?.assessment?.sectionId
    ) {
      return;
    }
    if (ca_base_station_details) {
      navigate(CLICKER_ASSESSMENT_START);
      dispatch(setSessionStartType(startType));
    } else {
      dispatch(
        setToastInfo({
          label: 'No Base Station found for this class',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const getBaseStationDetails = async () => {
    if (
      !current_assessmemt_classStats?.assessment?.classId ||
      !current_assessmemt_classStats?.assessment?.sectionId
    ) {
      return;
    }
    try {
      const stationDetails = await getBaseStationConnectionStatus({
        teacherId: teacher_id,
        classId: current_assessmemt_classStats?.assessment?.classId,
        sectionId: current_assessmemt_classStats?.assessment?.sectionId,
      });
      if (stationDetails) {
        //deciding the connected classroom flow
        dispatch(setCABaseStationDetails(stationDetails));
        authenticateAnonymously();
        // navigate(CLICKER_ASSESSMENT_START);
        // dispatch(setSessionStartType(startType));
      } else {
        dispatch(
          setToastInfo({
            label: 'No Base Station found for this class',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (error) {
      console.error('Error in connectedClassStartCTA:', error);
      dispatch(
        setToastInfo({
          label: 'Failed to start connected class',
          variant: 'error',
          open: true,
        })
      );
    }
  };
  // ------------------------------------------------------------

  const getAssessmentClassStats = async (changedAttemptInfo?: AttemptStats) => {
    try {
      setShowAppLoader(true);
      const response =
        await LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler.getAssessmentClassStats(
          {
            teacherId: BigInt(teacher_id),
            assessmentId: selected_assessment_id,
            attemptNumber: changedAttemptInfo?.attemptNumber,
          }
        );
      if (response.data) {
        // console.log(response.data);
        const StatusObj: IStatusObj = {};
        response.data.studentPerformanceInfo.forEach((e) => {
          const id = e.studentId.toString();
          StatusObj[id] = {
            studentId: e.studentId,
            isSubmitted: e.isSubmitted || false,
            marks: convertPercentageToMarks(
              e.scorePercent,
              response.data?.assessment?.maxMarks
            ),
          };
        });
        dispatch(setCurrentAssessmentClassStats(response.data));
        dispatch(
          setClickerAssessmentInfo({
            ...clicker_assessment_info,
            statusInfo: StatusObj,
            selected_attempt: changedAttemptInfo,
          })
        );

        setShowAppLoader(false);
      } else {
        // dispatch(setClickerAssessmentInfo(undefined));
        // dispatch(setCurrentAssessmentClassStats(undefined));
        dispatch(
          setToastInfo({
            label: `Data for Attempt ${changedAttemptInfo?.attemptNumber} could not be fetched.`,
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      setShowAppLoader(false);
      console.log(err);
      dispatch(
        setToastInfo({
          label: `Data for Attempt ${changedAttemptInfo?.attemptNumber} could not be fetched.`,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // ------------------------------------------------------------

  const sessionOngoingOrPaused = (): boolean => {
    const attempts = current_assessmemt_classStats?.attemptInfo || [];
    if (attempts.length === 0) {
      return false;
    } else {
      const latestAttempt = attempts.sort((a, b) => {
        return b.attemptNumber - a.attemptNumber;
      })[0];
      return (
        latestAttempt.attemptStatus ===
          SessionStatusEnum.SESSION_STATUS_ONGOING ||
        latestAttempt.attemptStatus === SessionStatusEnum.SESSION_STATUS_PAUSED
      );
    }
  };

  // ------------------------------------------------------------

  const getSecondaryTitle = () => {
    const className = current_assessmemt_classStats?.assessment?.class;
    const sectionName = current_assessmemt_classStats?.assessment?.section;
    const subjectName = current_assessmemt_classStats?.assessment?.subject;
    const moduleName = current_assessmemt_classStats?.assessment?.moduleName;
    if (
      current_assessmemt_classStats?.assessment?.taskType ===
        TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM ||
      current_assessmemt_classStats?.assessment?.taskType ===
        TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_AUTOMATED
    ) {
      const uniqueSubjects = new Set(
        current_assessmemt_classStats?.assessment?.subjects
      );
      const uniqueSubjectsArray = Array.from(uniqueSubjects).join(', ');
      return `${className} ${sectionName} ${uniqueSubjectsArray}`;
    } else {
      return `${className} ${sectionName} ${subjectName} | ${moduleName}`;
    }
  };

  // ------------------------------------------------------------
  useEffect(() => {
    if (teacher_id) {
      getBaseStationDetails();
    }
    setEndTimeValues({
      timeValue: `${deadLine?.getHours()} : ${deadLine?.getMinutes()}`,
      dateValue:
        deadLine
          ?.toLocaleString('en-UK', { timeZone: 'Asia/Kolkata' })
          .slice(0, 10)
          .split('/')
          .reverse()
          .join('-') ||
        new Date(
          Date.now() - new Date().getTimezoneOffset() * 60000
        ).toISOString(),
    });
  }, [current_assessmemt_classStats?.assessment?.assessmentId]);

  console.log('baseStationData leftPanel', caBaseStationData);

  return (
    <Box sx={styles.root}>
      <HeaderWithProgressBar
        imagePath={getMediaBasePath(
          current_assessmemt_classStats?.assessment?.assessmentPosterImgUrl,
          'processedMediaBucket'
        )}
        primaryTitle={
          current_assessmemt_classStats?.assessment?.assessmentTitle || 'title'
        }
        secondaryTitle={getSecondaryTitle()}
        progressBarScore={
          Math.round(
            Number(
              current_assessmemt_classStats?.assessment?.classScore?.toFixed(2)
            )
          ) || 0
        }
      />

      {isMobile && (
        <OverviewBox
          assessmentTargetDate={
            current_assessmemt_classStats?.assessment?.assessmentTargetDate
          }
          difficultyLevel={
            current_assessmemt_classStats?.assessment?.difficultyLevel
          }
          numberOfQuestions={
            current_assessmemt_classStats?.assessment?.assessmentContentInfo
              ?.numberOfQuestions
          }
          viewAssessmentCTA={() => navigate(`${ASSESSMENT_VIEW}/assigned`)}
        />
      )}

      <ConnectedClassStartBox
        isOnline={ca_base_station_details?.isOnline}
        primaryButton={{
          text: 'START',
          clickHandler: () => connectedClassStartResumeCTA('start'),
        }}
        secondaryButton={
          sessionOngoingOrPaused()
            ? {
                text: 'RESUME TEST',
                clickHandler: () => connectedClassStartResumeCTA('resume'),
              }
            : undefined
        }
      />

      <SelectMenu
        placeholder="select"
        value={
          current_assessmemt_classStats?.attemptInfo?.filter(
            (e) =>
              e.attemptStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
          ).length === 0
            ? 'No Attempts'
            : `Attempt ${clicker_assessment_info?.selected_attempt?.attemptNumber}`
        }
        disabled={
          current_assessmemt_classStats?.attemptInfo.filter(
            (e) =>
              e.attemptStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
          ).length === 0
        }
        onChange={(value: string) => {
          getAssessmentClassStats(
            current_assessmemt_classStats?.attemptInfo?.find(
              (item) => item.attemptNumber === Number(value.split(' ')[1])
            )
          );
        }}
        optionList={createIOptionListFromAttemptStats(
          current_assessmemt_classStats?.attemptInfo.filter(
            (e) =>
              e.attemptStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
          )
        )}
        rootStyles={{
          width: { xs: '100%', md: '100%', lg: '100%' },
          height: { xs: pxToRem(55), md: pxTovW(75), lg: pxTovW(65) },
        }}
        selectedBold
        menuItemStyles={{
          padding: { xs: pxToRem(10), md: pxTovW(20), lg: pxTovW(20) },
        }}
      />

      <Box sx={styles.infoBox}>
        <Box sx={styles.deadlineBox}>
          <Box
            sx={{
              ...styles.timeDetails,
              borderRight: '1px solid #c7c7c7',
              height: isIpadOnly ? pxTovW(100) : 'unset',
            }}
          >
            <Typography variant="h4" fontWeight="regular">
              {current_assessmemt_classStats?.assessment
                ?.assessmentCommenceDate &&
              remainingTimeInHoursAndMins(
                current_assessmemt_classStats?.assessment.assessmentCommenceDate
              ) === '0 mins'
                ? ' Remaining Time'
                : 'Homework Starts'}
            </Typography>

            <Typography
              variant={isIpadOnly ? 'h4' : 'h2'}
              fontWeight="bold"
              sx={{ fontSize: { xs: pxToRem(16), md: pxTovW(26) } }}
            >
              {current_assessmemt_classStats?.assessment
                ?.assessmentCommenceDate &&
              remainingTimeInHoursAndMins(
                current_assessmemt_classStats?.assessment.assessmentCommenceDate
              ) === '0 mins'
                ? remainingTimeInHoursAndMins(
                    current_assessmemt_classStats?.assessment
                      ?.assessmentTargetDate
                  )
                : `${formatSecondsToDateTimeString(
                    current_assessmemt_classStats?.assessment
                      ?.assessmentCommenceDate?.seconds
                  )}`}
            </Typography>
          </Box>

          <Box
            sx={{
              ...styles.timeDetails,
              paddingLeft: { xs: pxToRem(25), md: pxTovW(40), lg: pxTovW(32) },
              height: isIpadOnly ? pxTovW(100) : 'unset',
            }}
          >
            <Typography variant="h4" fontWeight="regular">
              Deadline
            </Typography>

            <Typography
              variant={isIpadOnly ? 'h4' : 'h2'}
              fontWeight="bold"
              sx={{ fontSize: { xs: pxToRem(16), md: pxTovW(26) } }}
            >
              {formatSecondsToDateTimeString(
                current_assessmemt_classStats?.assessment?.assessmentTargetDate
                  ?.seconds
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ border: '1px dashed #ACACAC' }}></Box>

        <Box sx={styles.endExtendButtonBox}>
          <LinkButton
            onClick={async () => {
              setEndTestPopup(true);
              await interactionEvent({
                context: 'Clicker_assessment',
                name: 'END_TEST',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            textColor="#F54040"
          >
            End Test
          </LinkButton>

          <LinkButton onClick={extendDeadlineCTA} textColor="primary.main">
            Extend Deadline
          </LinkButton>
        </Box>
      </Box>

      <ActionsPopup
        open={endTestPopup}
        handleClose={() => {
          setEndTestPopup(false);
        }}
        fontSmall
        iconName="test-white-pencil"
        popupText="Are you sure want to end test?"
        splitText="Continue?"
        splitTextColor="primary.main"
        yesClickHandler={async () => {
          handleEndTest();
        }}
        noClickHandler={async () => {
          setEndTestPopup(false);
        }}
      />

      <ReminderPopup
        handleClose={async () => {
          setEndTimeValues({
            timeValue: `${deadLine?.getHours()} : ${deadLine?.getMinutes()}`,
            dateValue:
              deadLine
                ?.toLocaleString('en-UK', { timeZone: 'Asia/Kolkata' })
                .slice(0, 10)
                .split('/')
                .reverse()
                .join('-') ||
              new Date(
                Date.now() - new Date().getTimezoneOffset() * 60000
              ).toISOString(),
          });
          setExtendDeadlinePopup(false);
        }}
        open={extendDeadlinePopup}
        iconName="schedule"
        popupComponent={
          <Box
            sx={{
              height: '80%',
              width: '100%',
              justifyContent: 'space-between',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-around',
              }}
            >
              <Box
                sx={{
                  width: '40%',
                }}
              >
                <DatePicker
                  minDate={new Timestamp(
                    current_assessmemt_classStats?.assessment?.assessmentTargetDate
                  )
                    .toDate()
                    .toLocaleString('en-UK', { timeZone: 'Asia/Kolkata' })
                    .slice(0, 10)
                    .split('/')
                    .reverse()
                    .join('-')}
                  value={`${endTimeValues.dateValue}`}
                  handleDateChange={(date) =>
                    setEndTimeValues({
                      ...endTimeValues,
                      dateValue: date,
                    })
                  }
                />
              </Box>

              <Box sx={{ width: '40%' }}>
                <TimePicker
                  minTime={
                    `${endTimeValues.dateValue.slice(0, 10)}` ===
                    new Timestamp(
                      current_assessmemt_classStats?.assessment?.assessmentCommenceDate
                    )
                      .toDate()
                      ?.toLocaleString('en-UK', { timeZone: 'Asia/Kolkata' })
                      .slice(0, 10)
                      .split('/')
                      .reverse()
                      .join('-')
                      ? `${new Timestamp(
                          current_assessmemt_classStats?.assessment?.assessmentTargetDate
                        )
                          .toDate()
                          .getHours()}:${new Timestamp(
                          current_assessmemt_classStats?.assessment?.assessmentTargetDate
                        )
                          .toDate()
                          .getMinutes()}`
                      : undefined
                  }
                  fullWidth
                  date={`${endTimeValues.dateValue}`}
                  value={endTimeValues.timeValue}
                  handleTimeChange={(time) => {
                    setEndTimeValues({ ...endTimeValues, timeValue: time });
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: {
                  xs: `${pxToRem(0)} ${pxToRem(14)} ${pxToRem(14)} ${pxToRem(
                    22
                  )}`,

                  md: `${pxToRem(0)} ${pxToRem(26)} ${pxToRem(36)} ${pxToRem(
                    26
                  )}`,
                },
              }}
            >
              <PrimaryButton fullWidth onClick={handleExtendDeadline}>
                <Typography
                  variant={isIpadOnly ? 'h2' : 'h4'}
                  fontFamily="poppins"
                  color="white"
                  fontWeight={600}
                >
                  CONFIRM
                </Typography>
              </PrimaryButton>
            </Box>
          </Box>
        }
        heading={
          <Typography variant={isMobile ? 'h3' : 'h1'} fontWeight="medium">
            Extend Deadline
          </Typography>
        }
      />
    </Box>
  );
};

// ------------------------------------------------------------
